// Libraries
import React from 'react';

// Supermove
import {useNavigationContext} from '@supermove/navigation';
import {Environment} from '@supermove/sdk';

// App
import ProjectAttachmentsWidget from '@shared/modules/Project/components/widgets/ProjectAttachmentsWidget';
import ProjectCommentsWidget from '@shared/modules/Project/components/widgets/ProjectCommentsWidget';
import ProjectConversationsWidget from '@shared/modules/Project/components/widgets/ProjectConversationsWidget';
import ProjectDetailsWidget from '@shared/modules/Project/components/widgets/ProjectDetailsWidget';
import ProjectDocumentsWidget from '@shared/modules/Project/components/widgets/ProjectDocumentsWidget';
import ProjectEventLogWidget from '@shared/modules/Project/components/widgets/ProjectEventLogWidget';
import ProjectJobsWidget from '@shared/modules/Project/components/widgets/ProjectJobsWidget';
import ProjectTasksWidget from '@shared/modules/Project/components/widgets/ProjectTasksWidget';
import ProjectWorkflowsWidget from '@shared/modules/Project/components/widgets/ProjectWorkflowsWidget';
import useWidgetAnalytics from '@shared/modules/Project/components/widgets/hooks/useWidgetAnalytics';
import ProjectBlockKind from '@shared/modules/Project/enums/ProjectBlockKind';
import ProjectWidgetKind, {
  ProjectWidgetKindType,
} from '@shared/modules/Project/enums/ProjectWidgetKind';
import {
  SalesPageContentUrlFiltersType,
  SalesPageContentFilterKeys,
} from '@shared/modules/Sales/components/SalesPageContent';

const ProjectPaneContent = ({
  widget,
  urlFilters,
  contextFilters,
}: {
  widget?: ProjectWidgetKindType;
  urlFilters: SalesPageContentUrlFiltersType;
  contextFilters: Partial<SalesPageContentFilterKeys>;
}) => {
  const projectUuid = urlFilters.get('projectUuid');
  const jobUuid = urlFilters.get('jobUuid');
  const {params} = useNavigationContext();

  useWidgetAnalytics({
    contextName: 'Sales Leads List Project Pane',
    params,
  });

  const handleGoToJob = ({jobUuid}: {jobUuid: string}) => {
    urlFilters.handleReset({
      ...contextFilters,
      jobUuid,
      block: ProjectBlockKind.JobBlocks[0],
      widget: ProjectWidgetKind.JOB_DETAILS as ProjectWidgetKindType,
    });
  };
  const isOfficeApp = Environment.isOfficeApp();

  switch (widget) {
    case ProjectWidgetKind.PROJECT_DETAILS:
      return (
        <ProjectDetailsWidget
          projectUuid={projectUuid}
          block={urlFilters.get('block')}
          handleUpdateBlock={(block: string) => urlFilters.handleUpdate({block})}
        />
      );
    case ProjectWidgetKind.CONVERSATIONS:
      return <ProjectConversationsWidget projectUuid={projectUuid} />;
    case ProjectWidgetKind.JOB_DETAILS:
      return (
        <ProjectJobsWidget
          projectUuid={projectUuid}
          jobUuid={jobUuid}
          handleUpdateJob={(jobUuid: string) => urlFilters.handleUpdate({jobUuid})}
          handleResetWidget={() => urlFilters.handleReset(contextFilters)}
          handleGoToBilling={() =>
            urlFilters.handleReset({
              ...contextFilters,
              widget: ProjectWidgetKind.PROJECT_DETAILS as ProjectWidgetKindType,
              block: ProjectBlockKind.BILLING,
            })
          }
        />
      );
    case ProjectWidgetKind.TASKS:
      return (
        <ProjectTasksWidget
          projectUuid={projectUuid}
          handleResetTaskUuid={() => urlFilters.handleUpdate({taskUuid: undefined})}
        />
      );
    case ProjectWidgetKind.DOCUMENTS:
      return <ProjectDocumentsWidget projectUuid={projectUuid} handleGoToJob={handleGoToJob} />;
    case ProjectWidgetKind.ATTACHMENTS:
      return <ProjectAttachmentsWidget projectUuid={projectUuid} />;
    case ProjectWidgetKind.WORKFLOWS:
      return (
        <ProjectWorkflowsWidget
          projectUuid={projectUuid}
          handleUpdateWorkflowRunUuid={({workflowRunUuid}: {workflowRunUuid?: string}) => {
            urlFilters.handleUpdate({
              workflowRunUuid,
            });
          }}
          workflowRunUuid={urlFilters.get('workflowRunUuid')}
        />
      );
    case ProjectWidgetKind.COMMENTS:
      return <ProjectCommentsWidget projectUuid={projectUuid} />;
    case ProjectWidgetKind.EVENT_LOG:
      return (
        <ProjectEventLogWidget
          projectUuid={projectUuid}
          handleGoToJob={isOfficeApp ? handleGoToJob : undefined}
        />
      );
    default:
      return null;
  }
};

export default ProjectPaneContent;
